/* assets/rc-search.js: the site search. Replaces Shopify's own search, which skipped every several-color product (tag
   'hide') and so answered "shorts" with posters.

   Parts:
     1. Engine (no DOM): normalize, synonyms, scoring. Also runs in node for tests (module.exports).
     2. Index: sections/api-rc-search.liquid, fetched once per visit as /?section_id=api-rc-search the first time the
        search opens or is about to (pointerdown or focus on a search button), then kept in sessionStorage.
     3. Panel: a full-screen sheet on phones (below 700px, the header switch in assets/rc-header.css) and a full-width
        dropdown under the header from 700px. From 990px it has two columns: searches and links on the left, product
        cards on the right, as many across as assets/rc-search.css sets in --rcs-cols. Opened by any [data-rcs-open]
        (header icons, the phone menu's Search row).
     4. Results page: /search?q= (sections/rc-search-page.liquid) is filled by the same engine. Without JS, or if the
        index fails, that section shows Shopify's own results.
   Loaded deferred from snippets/rc-search.liquid (header) and the results page; nothing is fetched until a search opens.
   Tracking (window.rcEvent): search_open, search_query (settled, with counts), search_zero, search_result_click. */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- 1. engine */
  var GROUPS = {
    halftights: ['half tights', 'half tight', 'halftights', 'halftight', 'tights', 'tight'],
    splitshorts: ['split shorts', 'split short', 'splits', 'split', 'racing shorts', 'race shorts', 'racing short', 'race short'],
    shorts: ['shorts', 'short'],
    ld: ['4 inch', '4inch', '4in', 'ld'],
    rd: ['2.5 inch', '2.5inch', '2.5in', '2.5', 'rd'],
    tee: ['tee', 'tees', 't shirt', 't shirts', 'tshirt', 'tshirts'],
    shirt: ['shirt', 'shirts'],
    longsleeve: ['long sleeve', 'long sleeves', 'longsleeve'],
    hoodie: ['hoodie', 'hoodies', 'hoody', 'hoodys', 'sweatshirt', 'sweatshirts', 'hooded'],
    sweatpants: ['sweatpants', 'sweatpant', 'sweats', 'pants', 'joggers', 'jogger'],
    poster: ['poster', 'posters', 'print', 'prints', 'wall art'],
    singlet: ['singlet', 'singlets', 'tank', 'tanks', 'tank top', 'tank tops'],
    pre: ['pre', 'prefontaine', 'steve prefontaine'],
    usa: ['usa', 'america', 'american', 'united states'],
    camo: ['camo', 'camouflage'],
    sticker: ['sticker', 'stickers', 'decal', 'decals'],
    gift: ['gift', 'gifts', 'present', 'presents'],
    jacket: ['jacket', 'jackets', 'warm up', 'warmup', 'zip up'],
    gray: ['gray', 'grey'],
    twoinone: ['2 in 1', '2in1'],
    ret: ['return', 'returns', 'refund', 'refunds', 'exchange', 'exchanges'],
    track: ['track', 'tracking']
  };
  /* a field that has the left concept also counts as having the right ones */
  var IMPLIES = { splitshorts: ['shorts'], longsleeve: ['tee', 'shirt'], tee: ['shirt'], ld: ['splitshorts', 'shorts'] };
  /* query words that point at a concept without the index words doing the same: "compression" finds the half tights,
     but the compression shorts do not count as half tights */
  var QUERY_ONLY = { compression: 'halftights' };
  /* color families, so "blue" finds Storm and "pink" finds Cotton Candy (the Running page's color_families) */
  var FAMS = {
    'storm': 'blue', 'statue of liberty': 'black gray', 'camo': 'green gray', 'usa': 'red blue white',
    'cotton candy': 'pink blue', 'yellow jacket': 'yellow black', 'ice': 'blue', 'fire': 'red orange',
    'navy': 'blue', 'navy blue': 'blue', 'coffee': 'brown', 'forest green': 'green', 'army green': 'green',
    'lime green': 'green', 'volcano': 'red orange', 'usa camo': 'red blue camo'
  };
  var CATS = {
    'half-tights': ['Half tights', 'half tights'],
    'split-shorts': ['Split shorts', 'split shorts running shorts'],
    'shorts': ['Shorts', 'shorts running shorts'],
    'shirts': ['Running shirts', 'running shirts performance tee'],
    'singlets': ['Singlets', 'singlets running'],
    'tanks': ['Tanks', 'tanks'],
    'tees': ['Tees', 'tees t shirts'],
    'hoodies': ['Hoodies', 'hoodies'],
    'sweatpants': ['Sweatpants', 'sweatpants'],
    'posters': ['Posters', 'posters prints'],
    'stickers': ['Stickers', 'stickers'],
    'accessories': ['Accessories', 'accessories']
  };
  var W_TITLE = 10, W_COLOR = 7, W_CAT = 5, W_FAM = 3, W_TAG = 2, W_KEY = 6;

  var PHRASES = {}, MAXW = 1;
  Object.keys(GROUPS).forEach(function (c) {
    GROUPS[c].forEach(function (p) {
      PHRASES[p] = c;
      var n = p.split(' ').length;
      if (n > MAXW) MAXW = n;
    });
  });

  function normalize(s) {
    s = String(s == null ? '' : s).toLowerCase();
    if (s.normalize) s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    s = s.replace(/[\u2018\u2019'`]/g, '');
    s = s.replace(/(\d)\s*-\s*in\s*-\s*(\d)/g, '$1 in $2');
    s = s.replace(/(\d)\s*(?:"|\u201c|\u201d|\u2033|-?\s*inch(?:es)?\b|-?in\b(?!\s*-?\s*\d))/g, '$1 inch ');
    s = s.replace(/(\d)\.(\d)/g, '$1\u0001$2');
    s = s.replace(/[^a-z0-9\u0001]+/g, ' ').replace(/\u0001/g, '.');
    return s.trim();
  }
  function tokens(s) {
    s = normalize(s);
    return s ? s.split(' ') : [];
  }
  /* longest synonym phrase first; returns [{c: concept or '', toks: [...]}] */
  function chunk(toks, isQuery) {
    var out = [], i = 0;
    while (i < toks.length) {
      var hit = null;
      for (var n = Math.min(MAXW, toks.length - i); n >= 1; n--) {
        var ph = toks.slice(i, i + n).join(' ');
        if (PHRASES[ph]) { hit = { c: PHRASES[ph], toks: toks.slice(i, i + n) }; break; }
      }
      if (!hit) hit = { c: (isQuery && QUERY_ONLY[toks[i]]) || '', toks: [toks[i]] };
      out.push(hit);
      i += hit.toks.length;
    }
    return out;
  }
  function field(text, w) {
    var t = tokens(text), cons = {};
    chunk(t).forEach(function (h) {
      if (!h.c) return;
      cons[h.c] = 1;
      (IMPLIES[h.c] || []).forEach(function (x) { cons[x] = 1; });
    });
    return { t: t, c: cons, w: w };
  }
  /* one edit (insert, delete, substitute, swap) */
  function near(a, b) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > 1) return false;
    var i = 0;
    while (i < la && i < lb && a[i] === b[i]) i++;
    if (i === la && i === lb) return true;
    if (la === lb) {
      if (a.slice(i + 1) === b.slice(i + 1)) return true;
      return a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2);
    }
    return la > lb ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1);
  }
  /* how well one query word matches one field token: 1 exact, less for prefix or a typo */
  function tokScore(q, f, last, fz) {
    if (q === f) return 1;
    var ql = q.length;
    if (ql < f.length && f.lastIndexOf(q, 0) === 0) {
      if (ql >= 3 || (last && ql >= 1)) return (last ? 0.62 : 0.5) + 0.3 * ql / f.length;
      return 0;
    }
    if (fz && ql >= 5 && f.length >= 4) {
      if (near(q, f)) return 0.6;
      if (last && f.length > ql && near(q, f.slice(0, ql))) return 0.45;
    }
    return 0;
  }
  function has(arr, x) {
    for (var i = 0; i < arr.length; i++) if (arr[i] === x) return true;
    return false;
  }
  function termScore(term, fld) {
    /* the concept: 1 when the field also has the query's own words, less through another word of the group
       ("singlet" ranks the RD Singlet over the Muscle Tank, even with the singlets sold out) */
    if (term.c && fld.c[term.c]) {
      for (var w = 0; w < term.toks.length; w++) if (!has(fld.t, term.toks[w])) return 0.6;
      return 1;
    }
    if (term.toks.length > 1) {
      var sum = 0;
      for (var j = 0; j < term.toks.length; j++) {
        var b = 0;
        for (var k = 0; k < fld.t.length; k++) b = Math.max(b, tokScore(term.toks[j], fld.t[k], term.last && j === term.toks.length - 1, term.fz));
        if (!b) return 0;
        sum += b;
      }
      return 0.9 * sum / term.toks.length;
    }
    var q = term.toks[0], best = 0;
    for (var i = 0; i < fld.t.length; i++) {
      var s = tokScore(q, fld.t[i], term.last, term.fz);
      if (s > best) best = s;
      if (best === 1) break;
    }
    return term.c ? best * 0.9 : best;
  }
  /* typo matching only for a word the index does not have, whole or as a start ("shorts" must not find "shirts") */
  function known(vocab, w) {
    if (!vocab) return false;
    if (vocab.w[w]) return true;
    for (var i = 0; i < vocab.l.length; i++) if (vocab.l[i].lastIndexOf(w, 0) === 0) return true;
    return false;
  }
  function parse(q, vocab) {
    var t = tokens(q);
    var terms = chunk(t, true);
    if (terms.length) terms[terms.length - 1].last = !/\s$/.test(String(q));
    terms.forEach(function (term) {
      term.fz = false;
      term.toks.forEach(function (w) { if (w.length >= 5 && !known(vocab, w)) term.fz = true; });
    });
    return terms;
  }
  function scoreFields(terms, fields) {
    var total = 0;
    for (var i = 0; i < terms.length; i++) {
      var best = 0;
      for (var f = 0; f < fields.length; f++) {
        var s = termScore(terms[i], fields[f]) * fields[f].w;
        if (s > best) best = s;
      }
      if (!best) return 0;
      total += best;
    }
    return total;
  }

  function prepare(raw) {
    var idx = { p: [], h: [], co: [], ar: [] };
    (raw.p || []).forEach(function (p, i) {
      var cl = normalize(p.c);
      var cat = CATS[p.k] || ['', ''];
      idx.p.push({
        d: p, i: i, nt: normalize(p.t),
        f: [field(p.t, W_TITLE), field(p.c, W_COLOR), field(cat[1] + ' ' + (p.y || ''), W_CAT), field(FAMS[cl] || '', W_FAM), field(p.g, W_TAG)]
      });
    });
    (raw.h || []).forEach(function (h) { idx.h.push({ d: { t: h[0], u: h[1] }, nt: normalize(h[0]), f: [field(h[0], W_TITLE), field(h[2], W_KEY)] }); });
    (raw.co || []).forEach(function (c) { idx.co.push({ d: { t: c[0], u: c[1] }, nt: normalize(c[0]), f: [field(c[0], W_TITLE), field(c[2], W_KEY)] }); });
    (raw.ar || []).forEach(function (a) { idx.ar.push({ d: { t: a[0], u: a[1] }, nt: normalize(a[0]), f: [field(a[0], W_TITLE)] }); });
    var w = {};
    ['p', 'h', 'co', 'ar'].forEach(function (g) {
      idx[g].forEach(function (it) { it.f.forEach(function (fl) { fl.t.forEach(function (x) { w[x] = 1; }); }); });
    });
    idx.vocab = { w: w, l: Object.keys(w) };
    return idx;
  }
  /* in stock first, then sales; a sold-out color keeps a little of its sales weight, so it sits under the colors
     that can be bought but above weaker matches */
  function boostProduct(p) {
    var d = p.d, pop = 0, b = 0;
    if (d.b > 0) pop += Math.min(3, Math.log(1 + d.b) / Math.LN10 * 1.2);
    if (d.f === 'best') pop += 1.5;
    if (d.a) b = 3 + pop; else b = (d.r ? 0.5 : 0) + pop * 0.3;
    if (d.f === 'new') b += 0.5;
    return b;
  }
  function rank(list, terms, nq, isProduct) {
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var it = list[i], s = scoreFields(terms, it.f);
      if (!s) continue;
      if (nq && it.nt.indexOf(nq) !== -1) s += it.nt.lastIndexOf(nq, 0) === 0 ? 4 : 3;
      if (isProduct) s += boostProduct(it);
      out.push({ it: it, s: s });
    }
    out.sort(function (a, b) {
      return (b.s - a.s) || ((isProduct ? b.it.d.b - a.it.d.b : 0)) || (a.it.nt.length - b.it.nt.length);
    });
    return out.map(function (x) { return x.it.d; });
  }
  function search(idx, q) {
    var terms = parse(q, idx.vocab), nq = normalize(q);
    if (!terms.length) return { q: q, p: [], h: [], co: [], ar: [] };
    return { q: q, p: rank(idx.p, terms, nq, true), h: rank(idx.h, terms, nq), co: rank(idx.co, terms, nq), ar: rank(idx.ar, terms, nq) };
  }
  /* in stock, most units sold, one card per style and at most one (two for a longer list) per category, no stickers */
  function bestSellers(idx, n) {
    var seen = {}, per = {}, cap = n > 4 ? 2 : 1, out = [];
    idx.p.filter(function (p) { return p.d.a && p.d.k !== 'stickers' && p.d.k !== 'accessories'; }).sort(function (a, b) { return b.d.b - a.d.b; }).forEach(function (p) {
      if (out.length >= n || seen[p.nt] || (per[p.d.k] || 0) >= cap) return;
      seen[p.nt] = 1;
      per[p.d.k] = (per[p.d.k] || 0) + 1;
      out.push(p.d);
    });
    return out;
  }
  var Engine = { normalize: normalize, parse: parse, prepare: prepare, search: search, bestSellers: bestSellers, CATS: CATS };

  if (typeof document === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) module.exports = Engine;
    return;
  }

  /* ---------------------------------------------------------------- 2. index */
  var W = window, D = document;
  if (W.RCS) return;
  var KEY = 'rcs_idx_v1', idx = null, idxP = null;

  function track(n, p) { try { if (W.rcEvent) W.rcEvent(n, p); } catch (e) {} }
  function root() { return (W.Shopify && W.Shopify.routes && W.Shopify.routes.root) || '/'; }
  function loadIndex() {
    if (idx) return Promise.resolve(idx);
    if (idxP) return idxP;
    try {
      var c = sessionStorage.getItem(KEY);
      if (c) {
        var o = JSON.parse(c);
        if (o && o.v === 1 && o.p && o.p.length) { idx = prepare(o); return Promise.resolve(idx); }
      }
    } catch (e) {}
    idxP = fetch(root() + '?section_id=api-rc-search', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('index ' + r.status);
      return r.text();
    }).then(function (t) {
      var m = t.indexOf('{"v":'), e = t.indexOf('</script>', m);
      if (m < 0 || e < 0) throw new Error('index format');
      var o = JSON.parse(t.slice(m, e));
      try { sessionStorage.setItem(KEY, JSON.stringify(o)); } catch (er) {}
      idx = prepare(o);
      return idx;
    });
    idxP.catch(function () { idxP = null; });
    return idxP;
  }

  /* ---------------------------------------------------------------- shared markup */
  var CHIPS = [['Half tights', 'half tights'], ['Split shorts', 'split shorts'], ['Posters', 'posters'], ['Hoodies', 'hoodies'], ['Tees', 'tees'], ['Sweatpants', 'sweatpants'], ['Gifts', 'gifts']];
  var ICON_SEARCH = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M10.85 2a8.85 8.85 0 1 1 0 17.7 8.85 8.85 0 0 1 0-17.7ZM17.12 17.12 22 22" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
  var ICON_ARROW = '<svg viewBox="0 0 8 12" width="7" height="10" aria-hidden="true" focusable="false"><path d="M1.5 1l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.75"/></svg>';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }
  /* Black Friday switch (window.RC_BF, layout/theme.liquid): from Black Friday on (during and after) preorders are
     open, so a sold-out color on a restock master is "Sold out for now", not "Preorders open soon" */
  function restockLine() {
    var st = (window.RC_BF && window.RC_BF.state) || 'before';
    return st === 'during' || st === 'after' ? 'Sold out for now' : 'Preorders open soon';
  }
  function card(p, pos, type) {
    var badge = '';
    if (!p.a && !p.r) badge = '<span class="rcs-badge rcs-badge--out">Sold out</span>';
    else if (!p.a) badge = '';
    else if (p.f === 'best') badge = '<span class="rcs-badge rcs-badge--best">Bestseller</span>';
    else if (p.f === 'new') badge = '<span class="rcs-badge rcs-badge--new">New</span>';
    else if (p.cp) badge = '<span class="rcs-badge rcs-badge--sale">Sale</span>';
    var img = p.i ? '<img src="' + esc(p.i) + '" alt="" width="360" height="450" loading="lazy" decoding="async">' : '<span class="rcs-card__noimg">' + esc(p.t) + '</span>';
    var price = p.cp ? '<span class="rcs-sr">Sale price </span>' + esc(p.p) + ' <s><span class="rcs-sr">was </span>' + esc(p.cp) + '</s>' : esc(p.p);
    return '<li class="rcs-card"><a class="rcs-card__a" href="' + esc(p.u) + '" data-track="search-result-product" data-rcs-type="' + (type || 'product') + '" data-rcs-pos="' + pos + '">' +
      '<span class="rcs-card__media">' + img + badge + '</span>' +
      '<span class="rcs-card__t">' + esc(p.t) + '</span>' +
      (p.c ? '<span class="rcs-card__c">' + esc(p.c) + '</span>' : '') +
      '<span class="rcs-card__p">' + price + '</span>' +
      (!p.a && p.r ? '<span class="rcs-card__st">' + restockLine() + '</span>' : '') + '</a></li>';
  }
  function cards(list, type, cls) {
    return '<ul class="rcs-cards' + (cls ? ' ' + cls : '') + '" role="list">' + list.map(function (p, i) { return card(p, i + 1, type); }).join('') + '</ul>';
  }
  function chips(cls) {
    return '<div class="rcs-chips' + (cls ? ' ' + cls : '') + '">' + CHIPS.map(function (c, i) {
      return '<button type="button" class="rcs-chip" data-rcs-chip="' + esc(c[1]) + '" data-rcs-type="chip" data-rcs-pos="' + (i + 1) + '" data-track="search-chip">' + esc(c[0]) + '</button>';
    }).join('') + '</div>';
  }
  function links(title, list, type, n) {
    if (!list.length) return '';
    return '<div class="rcs-group"><h3 class="rcs-h">' + title + '</h3><ul class="rcs-links" role="list">' + list.slice(0, n).map(function (x, i) {
      return '<li><a class="rcs-link" href="' + esc(x.u) + '" data-track="search-result-' + type + '" data-rcs-type="' + type + '" data-rcs-pos="' + (i + 1) + '"><span>' + esc(x.t) + '</span>' + ICON_ARROW + '</a></li>';
    }).join('') + '</ul></div>';
  }
  /* the Popular searches as a plain list, for the left column of the wide panel */
  function popular() {
    return '<div class="rcs-group"><h3 class="rcs-h">Popular</h3><ul class="rcs-links" role="list">' + CHIPS.map(function (c, i) {
      return '<li><button type="button" class="rcs-link" data-rcs-chip="' + esc(c[1]) + '" data-rcs-type="chip" data-rcs-pos="' + (i + 1) + '" data-track="search-chip"><span>' + esc(c[0]) + '</span></button></li>';
    }).join('') + '</ul></div>';
  }
  /* the help pages people look for most, in this order, for the left column before anything is typed
     (titles as sections/api-rc-search.liquid writes them) */
  var HELP = ['Track my order', 'Size chart', 'Shipping', 'Returns and exchanges'];
  function helpList() {
    var out = [];
    HELP.forEach(function (t) {
      for (var i = 0; i < idx.h.length; i++) { if (idx.h[i].d.t === t) { out.push(idx.h[i].d); return; } }
    });
    return out;
  }
  function pills(list) {
    if (!list.length) return '';
    return '<div class="rcs-pills">' + list.slice(0, 3).map(function (c, i) {
      return '<a class="rcs-pill" href="' + esc(c.u) + '" data-track="search-result-collection" data-rcs-type="collection" data-rcs-pos="' + (i + 1) + '">' + esc(c.t) + ICON_ARROW + '</a>';
    }).join('') + '</div>';
  }
  function zero(q) {
    return '<div class="rcs-zero"><p class="rcs-zero__h">No results for &ldquo;' + esc(q) + '&rdquo;</p>' +
      '<p class="rcs-zero__p">Check the spelling or try one of these.</p>' + chips('rcs-chips--wrap') +
      '<a class="rcs-chat" href="/pages/contact-us" data-rc-help data-track="search-chat" data-rcs-type="chat" data-rcs-pos="1"><strong>Chat with us</strong><span>Tell us what you are looking for and someone on our team will help.</span></a></div>';
  }
  function searchUrl(q) { return root() + 'search?q=' + encodeURIComponent(q); }

  /* settled-query tracking, shared by the panel and the page */
  var tq = { t: 0, last: '' };
  function trackQuery(q, r, src) {
    clearTimeout(tq.t);
    var nq = normalize(q);
    if (nq.length < 2 || nq === tq.last) return;
    tq.t = setTimeout(function () {
      tq.last = nq;
      var props = { q: q.trim().slice(0, 80), results: r.p.length, help: r.h.length, guides: r.ar.length, src: src };
      track('search_query', props);
      if (!r.p.length) track('search_zero', props);
    }, 900);
  }
  function clickTracker(rootEl, src, getQ) {
    rootEl.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('[data-rcs-type]');
      if (!a || !rootEl.contains(a)) return;
      track('search_result_click', { q: (getQ() || '').trim().slice(0, 80), type: a.getAttribute('data-rcs-type'), pos: +a.getAttribute('data-rcs-pos') || 0, url: a.getAttribute('href') || '', src: src });
    });
  }

  /* ---------------------------------------------------------------- 3. panel */
  var P = null, opener = null, raf = 0;
  function build() {
    if (P) return P;
    var el = D.createElement('div');
    el.className = 'rcs';
    el.id = 'RcSearch';
    el.hidden = true;
    el.innerHTML =
      '<div class="rcs__scrim" data-rcs-close></div>' +
      '<div class="rcs__panel" role="dialog" aria-modal="true" aria-label="Search">' +
        '<form class="rcs__bar" role="search" action="' + esc(root()) + 'search" method="get">' +
          '<div class="rcs__field">' +
            '<span class="rcs__icon">' + ICON_SEARCH + '</span>' +
            '<label class="rcs-sr" for="RcSearchInput">Search the shop</label>' +
            '<input class="rcs__input" id="RcSearchInput" type="search" name="q" placeholder="Search half tights, shorts, posters" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" enterkeyhint="search" aria-controls="RcSearchBody">' +
            '<button type="button" class="rcs__clear" aria-label="Clear search" hidden><svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>' +
          '</div>' +
          '<button type="button" class="rcs__close" data-rcs-close data-track="search-close">Close</button>' +
        '</form>' +
        '<p class="rcs-sr" role="status" aria-live="polite" data-rcs-status></p>' +
        '<div class="rcs__body" id="RcSearchBody" data-rcs-body></div>' +
      '</div>';
    D.body.appendChild(el);
    P = {
      el: el, panel: el.querySelector('.rcs__panel'), form: el.querySelector('form'), input: el.querySelector('input'),
      clear: el.querySelector('.rcs__clear'), body: el.querySelector('[data-rcs-body]'), status: el.querySelector('[data-rcs-status]')
    };
    P.input.addEventListener('input', function () { schedule(); });
    /* scrolling the results means they are done typing: drop the keyboard (a tap still opens a result) */
    P.body.addEventListener('touchmove', function () { if (D.activeElement === P.input) P.input.blur(); }, { passive: true });
    P.clear.addEventListener('click', function () { P.input.value = ''; render(); P.input.focus(); });
    P.form.addEventListener('submit', function (e) {
      var q = P.input.value.trim();
      e.preventDefault();
      if (!q) return;
      track('search_result_click', { q: q.slice(0, 80), type: 'see_all', pos: 0, url: searchUrl(q), src: 'panel' });
      W.location.href = searchUrl(q);
    });
    /* the tracker goes first: a chip click re-renders the panel and takes its button out of the page */
    clickTracker(el, 'panel', function () { return P.input.value; });
    el.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-rcs-close]')) { close(); return; }
      /* Chat with us: snippets/rc-help-chat.liquid opens its panel from the same click; get out of its way */
      if (t.closest('[data-rc-help]')) { close(); return; }
      var chip = t.closest('[data-rcs-chip]');
      /* a tapped chip is a choice, not typing: close the keyboard so the results can be scrolled */
      if (chip) { P.input.value = chip.getAttribute('data-rcs-chip'); render(); P.input.blur(); return; }
      var retry = t.closest('[data-rcs-retry]');
      if (retry) { render(); }
    });
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
    });
    return P;
  }
  function schedule() {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(function () { raf = 0; render(); });
  }
  function placeDesktop() {
    /* the wrapper is a fixed box of no height; the bar inside it has the size */
    var h = D.querySelector('.header__wrapper .theme__header') || D.querySelector('.theme__header');
    var top = 0;
    if (h) top = Math.max(0, Math.round(h.getBoundingClientRect().bottom));
    if (top > W.innerHeight * 0.5) top = 0;
    P.el.style.setProperty('--rcs-top', top + 'px');
  }
  var WIDE = W.matchMedia ? W.matchMedia('(min-width: 990px)') : null;
  function cols() { return Math.max(2, parseInt(W.getComputedStyle(P.body).getPropertyValue('--rcs-cols'), 10) || 4); }
  function two(side, main) {
    return '<div class="rcs-cols"><div class="rcs-side">' + side + '</div><div class="rcs-main">' + main + '</div></div>';
  }
  function best(n) {
    return '<div class="rcs-group"><h3 class="rcs-h">Best sellers</h3>' + cards(bestSellers(idx, n), 'best', 'rcs-cards--four') + '</div>';
  }
  function products(r, q, max) {
    var html = '<div class="rcs-group"><div class="rcs-hrow"><h3 class="rcs-h">Products</h3>' +
      (r.p.length > max ? '<a class="rcs-all" href="' + esc(searchUrl(q)) + '" data-track="search-see-all" data-rcs-type="see_all" data-rcs-pos="0">See all ' + r.p.length + '</a>' : '') + '</div>' +
      cards(r.p.slice(0, max), 'product', 'rcs-cards--four') + '</div>';
    if (r.p.length > max) html += '<a class="rcs-more" href="' + esc(searchUrl(q)) + '" data-track="search-see-all" data-rcs-type="see_all" data-rcs-pos="0">See all ' + r.p.length + ' results</a>';
    return html;
  }
  function render() {
    var q = P.input.value, nq = normalize(q), wide = !!(WIDE && WIDE.matches);
    P.clear.hidden = !q;
    if (!idx) {
      P.body.innerHTML = '<p class="rcs-wait">Loading search&hellip;</p>';
      loadIndex().then(function () { if (P && !P.el.hidden) render(); }, function () {
        P.body.innerHTML = '<div class="rcs-zero"><p class="rcs-zero__p">Search did not load. Check your connection.</p><button type="button" class="rcs-chip" data-rcs-retry>Try again</button></div>';
      });
      return;
    }
    /* one row of cards before anything is typed, two rows of results */
    var n = cols(), html = '';
    P.cols = n;
    if (!nq) {
      P.body.innerHTML = wide ? two(popular() + links('Help', helpList(), 'page', 4), best(n)) :
        '<div class="rcs-group"><h3 class="rcs-h">Popular</h3>' + chips() + '</div>' + best(n);
      P.status.textContent = '';
      return;
    }
    var r = search(idx, q);
    if (wide) {
      var side = links('Shop', r.co, 'collection', 4) + links('Help', r.h, 'page', 3) + links('Guides', r.ar, 'guide', 3);
      if (r.p.length) html = two(side || popular(), products(r, q, n * 2));
      else html = two(side || links('Help', helpList(), 'page', 4), zero(q) + best(n));
    } else if (r.p.length) {
      html += pills(r.co) + products(r, q, n * 2) + links('Help', r.h, 'page', 3) + links('Guides', r.ar, 'guide', 3);
    } else {
      html += zero(q) + pills(r.co) + links('Help', r.h, 'page', 3) + links('Guides', r.ar, 'guide', 3) + best(n);
    }
    P.body.innerHTML = html;
    P.body.scrollTop = 0;
    P.status.textContent = r.p.length ? r.p.length + (r.p.length === 1 ? ' product' : ' products') : 'No products found';
    trackQuery(q, r, 'panel');
  }
  function open(from, el) {
    build();
    opener = el || D.activeElement;
    if (from === 'menu') {
      var x = D.querySelector('.drawer--header .drawer__close');
      if (x) x.click();
    }
    placeDesktop();
    P.el.hidden = false;
    D.documentElement.classList.add('rcs-open');
    P.input.focus({ preventScroll: true });
    render();
    requestAnimationFrame(function () { if (P.el) P.el.classList.add('is-in'); });
    track('search_open', { from: from || 'header', page: (W.location.pathname || '').slice(0, 80) });
  }
  function close() {
    if (!P || P.el.hidden) return;
    P.el.classList.remove('is-in');
    P.el.hidden = true;
    D.documentElement.classList.remove('rcs-open');
    if (opener && opener.focus && D.contains(opener)) { try { opener.focus({ preventScroll: true }); } catch (e) {} }
    opener = null;
  }
  function wireOpeners() {
    D.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-rcs-open]');
      if (!b) return;
      e.preventDefault();
      if (P && !P.el.hidden) { close(); return; }
      open(b.getAttribute('data-rcs-open') || 'header', b);
    });
    var pre = function (e) { if (e.target.closest && e.target.closest('[data-rcs-open]')) loadIndex().catch(function () {}); };
    D.addEventListener('pointerdown', pre, { passive: true });
    D.addEventListener('focusin', pre);
    /* a resize can cross 990px (one column or two) or change how many cards fit across: draw the panel again */
    W.addEventListener('resize', function () {
      if (!P || P.el.hidden) return;
      placeDesktop();
      if (idx && cols() !== P.cols) render();
    }, { passive: true });
    if (WIDE && WIDE.addEventListener) WIDE.addEventListener('change', function () { if (P && !P.el.hidden && idx) render(); });
  }

  /* ---------------------------------------------------------------- 4. results page */
  function page(rootEl) {
    var input = rootEl.querySelector('[data-rcs-page-input]'), out = rootEl.querySelector('[data-rcs-page-out]'),
      fallback = rootEl.querySelector('[data-rcs-fallback]'), status = rootEl.querySelector('[data-rcs-page-status]'),
      form = rootEl.querySelector('form'), cat = '';
    function draw() {
      var q = input.value, r = search(idx, q), html = '', list = r.p;
      if (!normalize(q)) {
        out.innerHTML = '<div class="rcs-group"><h2 class="rcs-h">Popular</h2>' + chips() + '</div><div class="rcs-group"><h2 class="rcs-h">Best sellers</h2>' + cards(bestSellers(idx, 8), 'best', 'rcs-cards--page') + '</div>';
        status.textContent = '';
        return;
      }
      if (r.p.length) {
        var counts = {}, order = [];
        r.p.forEach(function (p) { if (!counts[p.k]) { counts[p.k] = 0; order.push(p.k); } counts[p.k]++; });
        if (cat && !counts[cat]) cat = '';
        if (cat) list = r.p.filter(function (p) { return p.k === cat; });
        html += '<p class="rcs-page__count">' + r.p.length + (r.p.length === 1 ? ' result' : ' results') + ' for &ldquo;' + esc(q.trim()) + '&rdquo;</p>';
        if (order.length > 1) {
          html += '<div class="rcs-chips rcs-chips--filter" role="group" aria-label="Filter by category"><button type="button" class="rcs-chip' + (cat ? '' : ' is-on') + '" data-rcs-cat="" aria-pressed="' + (!cat) + '">All</button>' +
            order.map(function (k) {
              return '<button type="button" class="rcs-chip' + (cat === k ? ' is-on' : '') + '" data-rcs-cat="' + k + '" aria-pressed="' + (cat === k) + '" data-track="search-filter">' + esc((CATS[k] || [k])[0]) + ' <span>' + counts[k] + '</span></button>';
            }).join('') + '</div>';
        }
        html += pills(r.co) + cards(list, 'product', 'rcs-cards--page');
        html += '<div class="rcs-page__more">' + links('Help', r.h, 'page', 4) + links('Guides', r.ar, 'guide', 6) + '</div>';
      } else {
        html += zero(q.trim()) + pills(r.co) + '<div class="rcs-page__more">' + links('Help', r.h, 'page', 4) + links('Guides', r.ar, 'guide', 6) + '</div>' +
          '<div class="rcs-group"><h2 class="rcs-h">Best sellers</h2>' + cards(bestSellers(idx, 4), 'best', 'rcs-cards--page') + '</div>';
      }
      out.innerHTML = html;
      status.textContent = r.p.length ? r.p.length + ' products' : 'No products found';
      trackQuery(q, r, 'page');
    }
    function sync() {
      try { history.replaceState(history.state, '', input.value.trim() ? searchUrl(input.value.trim()) : root() + 'search'); } catch (e) {}
    }
    out.innerHTML = '<p class="rcs-wait">Loading results&hellip;</p>';
    loadIndex().then(function () {
      if (fallback) fallback.hidden = true;
      draw();
      var t = 0;
      input.addEventListener('input', function () { cat = ''; clearTimeout(t); t = setTimeout(function () { draw(); sync(); }, 120); });
      D.addEventListener('touchmove', function () { if (D.activeElement === input) input.blur(); }, { passive: true });
      form.addEventListener('submit', function (e) { e.preventDefault(); input.blur(); draw(); sync(); });
      clickTracker(rootEl, 'page', function () { return input.value; });
      rootEl.addEventListener('click', function (e) {
        var c = e.target.closest('[data-rcs-cat]');
        if (c) { cat = c.getAttribute('data-rcs-cat'); input.blur(); draw(); return; }
        var chip = e.target.closest('[data-rcs-chip]');
        if (chip) { input.value = chip.getAttribute('data-rcs-chip'); cat = ''; input.blur(); draw(); sync(); }
      });
    }, function () {
      out.innerHTML = '';
      if (fallback) { fallback.hidden = false; rootEl.classList.add('rcs-page--fallback'); }
    });
  }

  W.RCS = { open: open, close: close, search: function (q) { return idx ? search(idx, q) : null; }, load: loadIndex, engine: Engine };
  function init() {
    wireOpeners();
    var pg = D.querySelector('[data-rcs-page]');
    if (pg) page(pg);
  }
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', init); else init();
})();
